import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FavoritesService } from '@/api/user-portal-service';
import { queryKeys } from '@/common/queryClient';
import { useToast } from '@/hooks/use-toast';
import type { UserApplicationsResult } from '@/hooks/use-user-portal';

interface ToggleFavoriteParams {
  applicationId: string;
  isFavorite: boolean;
}

function patchFavorite(
  current: UserApplicationsResult | undefined,
  applicationId: string,
  isFavorite: boolean
): UserApplicationsResult | undefined {
  if (!current) {
    return current;
  }

  return {
    ...current,
    items: current.items.map(app =>
      app.app_id === applicationId ? { ...app, is_favorite: isFavorite } : app
    ),
  };
}

export function useToggleFavorite() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation<void, Error, ToggleFavoriteParams>({
    mutationFn: async ({ applicationId, isFavorite }) => {
      await FavoritesService.toggleFavorite(applicationId, {
        is_favorite: isFavorite,
      });
    },
    onMutate: async ({ applicationId, isFavorite }) => {
      const queryKey = queryKeys.applications.list();
      await queryClient.cancelQueries({ queryKey });

      queryClient.setQueryData<UserApplicationsResult>(queryKey, current =>
        patchFavorite(current, applicationId, isFavorite)
      );
    },
    onError: (error, { applicationId, isFavorite }) => {
      queryClient.setQueryData<UserApplicationsResult>(
        queryKeys.applications.list(),
        current => patchFavorite(current, applicationId, !isFavorite)
      );
      toast.error({
        title: 'Unable to Update Favorite',
        message:
          error.message || 'Unable to update favorite. Please try again.',
      });
    },
  });
}
